/**
 * TweenManager - world-level system managing Tween.js tweens with centralized update loop.
 */

import * as TWEEN from '@tweenjs/tween.js';
import * as THREE from 'three';

import { ObjectTracker } from './ObjectTracker.js';

import type { World } from '../game/World.js';

export class TweenManager {
  private world: World;
  private currentTime: number = 0;
  private tweens: Set<TWEEN.Tween<any>> = new Set();

  constructor(world: World) {
    ObjectTracker.track(this);
    this.world = world;
  }

  /** Creates a new tween and automatically tracks it */
  public createTween<T extends Record<string, any>>(object: T): TWEEN.Tween<T> {
    const tween = new TWEEN.Tween(object);
    this.tweens.add(tween);

    // Auto-remove when complete or stopped
    tween.onComplete(() => {
      this.tweens.delete(tween);
    });
    tween.onStop(() => {
      this.tweens.delete(tween);
    });

    return tween;
  }

  /** Manually add an existing tween to be managed */
  public addTween(tween: TWEEN.Tween<any>): void {
    this.tweens.add(tween);
  }

  /** Remove a tween from management (doesn't stop it) */
  public removeTween(tween: TWEEN.Tween<any>): void {
    this.tweens.delete(tween);
  }

  /** Stop and remove a tween */
  public stopTween(tween: TWEEN.Tween<any>): void {
    tween.stop();
    this.tweens.delete(tween);
  }

  /**
   * Update all managed tweens with delta time.
   * Called automatically by the world.
   */
  @THREE.profile
  public update(deltaTime: number): void {
    this.currentTime += deltaTime * 1000; // Convert to milliseconds

    // Update all tweens with explicit time
    for (const tween of this.tweens) {
      tween.update(this.currentTime);
    }
  }

  /** @returns Current time in milliseconds */
  public getCurrentTime(): number {
    return this.currentTime;
  }

  /** Stop all tweens and clear */
  public destroy(): void {
    for (const tween of this.tweens) {
      tween.stop();
    }
    this.tweens.clear();
  }

  /** @returns Number of active tweens */
  public getActiveTweenCount(): number {
    return this.tweens.size;
  }
}

